import { useState, useEffect } from "react";

const COLORS = ["#00628F", "#00628F", "#F9A01B", "#157346", "#883476"];

export function HeroRadar({ data }) {
  if (!data || data.length === 0) return null;

  // < 3 dimensions: show horizontal bars
  if (data.length < 3) return <BarFallback data={data} />;

  const n = data.length;
  const cx = 140, cy = 115, R = 85;
  const angles = data.map((_, i) => (-Math.PI / 2) + (i * 2 * Math.PI / n));

  const point = (i, pct) => ({
    x: cx + (pct / 100) * R * Math.cos(angles[i]),
    y: cy + (pct / 100) * R * Math.sin(angles[i]),
  });

  // Outer polygon (100%)
  const outerPts = data.map((_, i) => point(i, 100));
  const outerPath = outerPts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ") + "Z";

  // Mid polygon (50%)
  const midPts = data.map((_, i) => point(i, 50));
  const midPath = midPts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ") + "Z";

  // User polygon
  const userPts = data.map((d, i) => point(i, d.value));
  const userPath = userPts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ") + "Z";

  // Label positions (further out)
  const labelR = R + 22;
  const labels = data.map((d, i) => ({
    x: cx + labelR * Math.cos(angles[i]),
    y: cy + labelR * Math.sin(angles[i]),
    text: d.dimension,
  }));

  return (
    <div className="flex flex-col items-center" data-testid="hero-radar">
      <svg viewBox="0 0 280 240" style={{ width: "100%", maxWidth: 280 }}>
        <defs>
          <linearGradient id="radarFill" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#00628F" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#2A9D8F" stopOpacity="0.3" />
          </linearGradient>
        </defs>

        {/* Outer frame */}
        <path d={outerPath} fill="#00628F" fillOpacity="0.05" stroke="#E2D9CB" strokeWidth="1" />
        {/* Mid frame (50%) dashed */}
        <path d={midPath} fill="none" stroke="#E2D9CB" strokeWidth="0.8" strokeDasharray="4 3" />
        {/* Axis lines */}
        {outerPts.map((p, i) => (
          <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#E2D9CB" strokeWidth="0.5" />
        ))}
        {/* User polygon */}
        <path d={userPath} fill="url(#radarFill)" stroke="#00628F" strokeWidth="2" strokeOpacity="0.8" strokeLinejoin="round">
          <animate attributeName="d"
            from={data.map((_, i) => `${i === 0 ? "M" : "L"}${cx},${cy}`).join(" ") + "Z"}
            to={userPath} dur="0.8s" fill="freeze" />
        </path>
        {/* Vertex dots */}
        {userPts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="4" fill={data[i].color} stroke="white" strokeWidth="1.5">
            <animate attributeName="cx" from={cx} to={p.x} dur="0.8s" fill="freeze" />
            <animate attributeName="cy" from={cy} to={p.y} dur="0.8s" fill="freeze" />
          </circle>
        ))}
        {/* Labels */}
        {labels.map((l, i) => (
          <text key={i} x={l.x} y={l.y} textAnchor="middle" dominantBaseline="central"
            fill="#71644C" fontSize="11" fontWeight="600" fontFamily="Inter, sans-serif">
            {l.text}
          </text>
        ))}
      </svg>

      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-1">
        {data.map((d, i) => (
          <div key={i} className="flex items-center gap-1">
            <div className="rounded-full" style={{ width: 6, height: 6, background: d.color }} />
            <span style={{ fontSize: 10, color: "#71644C", fontWeight: 500 }}>{d.dimension}: {d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Fallback for 1-2 dimensions
function BarFallback({ data }) {
  return (
    <div className="space-y-2" data-testid="hero-bars">
      {data.map((d) => (
        <div key={d.dimension}>
          <div className="flex justify-between mb-1">
            <span style={{ fontSize: 11, color: "#71644C", fontWeight: 600 }}>{d.dimension}</span>
            <span style={{ fontSize: 11, color: "#2D2A24", fontWeight: 700 }}>{d.value}/100</span>
          </div>
          <div className="rounded-full" style={{ height: 8, background: "#E2D9CB" }}>
            <div className="rounded-full transition-all duration-1000" style={{ height: 8, width: `${d.value}%`, background: d.color }} />
          </div>
        </div>
      ))}
    </div>
  );
}
